import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useStore } from '@hub/helpers/use-store';
import { useColor } from '@hub/hooks';
import {
  Card,
  Col,
  Flex,
  Label,
  ProgressSlider,
  Row,
  TextComponent,
} from '@monorepo/react-components';
import { useResponsiveness } from '@monorepo/react-hooks';
import { IconsNames } from '@monorepo/types';
import {
  ConsistencyRuleResult,
  ConsistencyRules,
  ThemeColors,
} from '@monorepo/types';
import { AccountRulesCardIcon } from '../account-rules-icon/account-rules-icon';

import '../account-rules.scss';

interface Props {
  programId: string;
  isLoading: boolean;
  currentBestTradeProfit?: ConsistencyRuleResult;
}

const BestTradeProfitLimitRuleCard: FunctionComponent<Props> = ({
  programId,
  isLoading,
  currentBestTradeProfit = {
    isValid: true,
    current: 0,
  },
}) => {
  const { isValid, current } = currentBestTradeProfit;
  const { getColor } = useColor();
  const { isMobile } = useResponsiveness();
  const { t } = useTranslation(['dashboardTemplates']);

  const {
    dataStore: { programStore },
  } = useStore();

  const bestTradeProfitLimitRule = programStore.getRule(
    programId,
    ConsistencyRules.BestTradeProfitLimit,
  );

  const bestTradeProfitLimitValue = bestTradeProfitLimitRule?.value;

  if (!bestTradeProfitLimitValue) {
    return null;
  }

  return (
    <Card hoverable={false} className={'account-rules'}>
      <Row
        gutter={[5, 10]}
        className={classNames({ 'account-rules__row--mobile': isMobile })}
      >
        <Col
          span={8}
          sm={24}
          className={classNames({ 'account-rules__col--mobile': isMobile })}
        >
          <Flex
            justify={'space-between'}
            gap={5}
            className={classNames('account-rules__header', {
              'account-rules__mobile': isMobile,
            })}
          >
            <Col
              className={classNames({ 'account-rules__col--mobile': isMobile })}
            >
              <TextComponent
                color={getColor(ThemeColors.colorPrimary)}
                weight={600}
              >
                {t('dashboardTemplates:best_trade_profit_limit')}
              </TextComponent>
              <div
                className={classNames({
                  'account-rules__cap-limit--mobile': isMobile,
                })}
              >
                <Label color={'colorInfo'} bordered={false}>
                  {t('dashboardTemplates:cap_limit', {
                    value: bestTradeProfitLimitValue,
                  })}
                </Label>
              </div>
            </Col>
            <Row>
              <AccountRulesCardIcon iconName={IconsNames.Umbrella} />
            </Row>
          </Flex>
        </Col>

        <Col span={16} sm={24}>
          <Flex
            justify={'end'}
            vertical
            className={'account-rules__slider-section'}
          >
            <Flex justify="space-between" align="end">
              <TextComponent loading={isLoading} level={4} responsive={false}>
                {current ? current.toFixed(0) : 0}%
              </TextComponent>
            </Flex>

            <ProgressSlider
              className={classNames('the5ers-progress-slider', {
                'the5ers-progress-slider--secondary': !isValid,
                'the5ers-progress-slider--success': isValid,
              })}
              disabled
              marks={{
                [bestTradeProfitLimitValue]: {
                  style: { color: 'grey', transform: 'translate(-50%, -160%)' },
                  label: `${bestTradeProfitLimitValue}%`,
                },
              }}
              tooltip={{ formatter: null }}
              primaryHandle={false}
              trackColor={null}
              value={current}
              max={bestTradeProfitLimitValue * 2}
              styles={{
                rail: {
                  background: `linear-gradient(to right, ${getColor(
                    ThemeColors.colorSuccess,
                  )} 50%, ${getColor(ThemeColors.colorSecondary)} 50%)`,
                },
              }}
            />
            <TextComponent level={8} responsive={false}>
              {t('dashboardTemplates:best_trade_limit', {
                value: bestTradeProfitLimitValue,
              })}
            </TextComponent>
          </Flex>
        </Col>
      </Row>
    </Card>
  );
};

export default BestTradeProfitLimitRuleCard;
